import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileUploadRepository } from '../../../domain/service/laboratory/file/file-upload-repository';
import {
  FileUploadResponse,
  GroupRecord
} from '../../../domain/model/laboratory/file/file-upload-response.model';
import { HttpErrorHandlerService } from '../../../core/error-handling/http-error-handler.service';
import { environment } from 'src/environments/environment';

/**
 * Backend DTO for file upload response
 */
interface FileUploadBackendResponse {
  fileName: string;
  uploadedBy: string;
  status: string;
  processedRecords: number;
  uploadedAt: string;
  message?: string;
  errors?: string[];
  groupRecords: GroupRecord[];
}

/**
 * File Upload Repository Adapter
 *
 * Implements the FileUploadRepository interface defined in the domain layer.
 * Handles HTTP communication for file uploads using multipart/form-data.
 *
 * Architecture Layer: Infrastructure
 * - Implements domain interface (Dependency Inversion)
 * - Uses HttpClient for HTTP operations
 * - Transforms backend DTOs to domain models
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadRepositoryAdapter extends FileUploadRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}`;
  private readonly errorHandler = inject(HttpErrorHandlerService);

  /**
   * Upload a file to the backend for processing
   *
   * @param file - The file to upload
   * @returns Observable with the upload response
   */
  uploadFile(file: File): Observable<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<FileUploadBackendResponse>(
        `${this.BASE_URL}/files/upload-xlsx`,
        formData
      )
      .pipe(
        map((response) => this.mapToModel(response)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('uploadFile', error)
        )
      );
  }

  /**
   * Map backend DTO to domain model
   */
  private mapToModel(dto: FileUploadBackendResponse): FileUploadResponse {
    return {
      fileName: dto.fileName,
      uploadedBy: dto.uploadedBy,
      status: dto.status,
      processedRecords: dto.processedRecords,
      uploadedAt: dto.uploadedAt,
      message: dto.message,
      errors: dto.errors,
      groupRecords: dto.groupRecords || []
    };
  }
}
